/** Values as the record editor shows them, and the client-side checks a value must
 *  pass before Save (the server re-validates everything; these only make a doomed
 *  save visible early).
 *
 *  Wire values follow BookingRecordChange: date `YYYY-MM-DD` and time `HH:MM` are the
 *  record's local wall clock, datetime `YYYY-MM-DDTHH:MM` likewise; times are always
 *  shown with their IANA zone, never browser-local.
 */
import { parseWallClock } from '@/utils/zonedTime';

import type { RecordField, RecordOptionRef, RecordWidgetProtocol } from './types';

export const EMPTY_DISPLAY = '—';

export function isEmptyValue(value: unknown): boolean {
  return (
    value === undefined ||
    value === null ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  );
}

// ---------------------------------------------------------------------------
// Wall-clock display (the zone is the record's; these never convert)
// ---------------------------------------------------------------------------

const wallFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

/** `2026-10-02` → "Fri 2 Oct" (the year when it is not `nowYear`). */
export function formatWallDate(value: string, nowYear = new Date().getFullYear()): string {
  const w = parseWallClock(value.slice(0, 10));
  if (!w) return value;
  const parts: Record<string, string> = {};
  for (const p of wallFormatter.formatToParts(Date.UTC(w.year, w.month - 1, w.day))) {
    parts[p.type] = p.value;
  }
  const bits = [parts.weekday, parts.day, parts.month];
  if (w.year !== nowYear) bits.push(parts.year);
  return bits.join(' ');
}

const TIME_RE = /^(\d{2}):(\d{2})/;

/** `09:30` → "09:30 Europe/London". */
export function formatWallTime(value: string, timezone: string | null): string {
  const m = TIME_RE.exec(value);
  const time = m ? `${m[1]}:${m[2]}` : value;
  return timezone ? `${time} ${timezone}` : time;
}

/** `2026-10-02T09:30` → "Fri 2 Oct 09:30 Europe/London". */
export function formatWallDateTime(
  value: string,
  timezone: string | null,
  nowYear?: number,
): string {
  const w = parseWallClock(value.slice(0, 16));
  if (!w) return value;
  const time = `${String(w.hour).padStart(2, '0')}:${String(w.minute).padStart(2, '0')}`;
  return `${formatWallDate(value.slice(0, 10), nowYear)} ${formatWallTime(time, timezone)}`;
}

/** `checked_in` → "Checked in" — the fallback for codes without a choice label. */
export function humanizeCode(value: string): string {
  const s = value.replace(/[_-]+/g, ' ').trim();
  return s ? s[0]!.toUpperCase() + s.slice(1) : value;
}

// ---------------------------------------------------------------------------
// Generic value display
// ---------------------------------------------------------------------------

export interface FormatContext {
  timezone: string | null;
  /** A picked record's name, by catalog field key and id. */
  optionRef: (fieldKey: string, id: unknown) => RecordOptionRef | undefined;
}

function refLabel(ctx: FormatContext, field: RecordField, id: unknown): string {
  return ctx.optionRef(field.key, id)?.label ?? `#${String(id)}`;
}

/** One value as text: the ledger's before → after, a "was …" caption, view mode. `widget`:
 *  the field's widget definition — its own `formatChange`, when it declares one, words a
 *  value that is neither empty, a boolean nor a choice. */
export function formatFieldValue(
  field: RecordField,
  value: unknown,
  ctx: FormatContext,
  widget?: RecordWidgetProtocol,
): string {
  if (isEmptyValue(value)) return EMPTY_DISPLAY;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  const choice = field.choices.find((c) => c.value === value);
  if (choice) return choice.label;
  if (widget?.formatChange) return widget.formatChange(value);
  switch (field.widget) {
    case 'date':
      return typeof value === 'string' ? formatWallDate(value) : String(value);
    case 'time':
      return typeof value === 'string' ? formatWallTime(value, ctx.timezone) : String(value);
    case 'datetime':
      return typeof value === 'string' ? formatWallDateTime(value, ctx.timezone) : String(value);
    case 'tags':
      return Array.isArray(value) ? value.map((id) => refLabel(ctx, field, id)).join(', ') : String(value);
    default:
      break;
  }
  if (typeof value === 'number') {
    if (field.widget === 'integer' || field.widget === 'enum') return String(value);
    // An id-valued field (tour, role, owner): its name when known.
    const ref = ctx.optionRef(field.key, value);
    return ref ? ref.label : `#${value}`;
  }
  if (typeof value === 'string') {
    // A queued action's state code (checked_in, not_started).
    if (field.kind === 'action' && /^[a-z_]+$/.test(value)) return humanizeCode(value);
    return value;
  }
  return JSON.stringify(value);
}

/** Tag-set changes as the ledger reads them: what was added, what was removed. */
export function diffIdSets(
  before: unknown,
  after: unknown,
): { added: Array<number | string>; removed: Array<number | string> } {
  const a = new Set(Array.isArray(before) ? (before as Array<number | string>) : []);
  const b = new Set(Array.isArray(after) ? (after as Array<number | string>) : []);
  return {
    added: [...b].filter((x) => !a.has(x)),
    removed: [...a].filter((x) => !b.has(x)),
  };
}

// ---------------------------------------------------------------------------
// Client validation
// ---------------------------------------------------------------------------

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Why `value` cannot be saved into `field`, or null. Only edited values are checked —
 *  a stored value the record already holds never blocks a save of something else. `from`
 *  (the change's baseline) tells a list-valued field which of its items were edited: the
 *  contacts list checks the rows it adds or edits only. `widget`: the field's widget
 *  definition — its own `checkValue`, when it declares one, replaces the generic checks. */
export function validateFieldValue(
  field: RecordField,
  value: unknown,
  from?: unknown,
  widget?: RecordWidgetProtocol,
): string | null {
  if (field.kind !== 'editable') return null;
  if (widget?.checkValue) return widget.checkValue(value, from);
  const empty = isEmptyValue(value);
  if (empty) {
    if (field.widget === 'tags' || field.widget === 'boolean') return null;
    return field.nullable ? null : 'Required';
  }
  switch (field.widget) {
    case 'integer': {
      if (typeof value !== 'number' || !Number.isInteger(value)) return 'Enter a whole number';
      if (field.min_value != null && value < field.min_value) return `At least ${field.min_value}`;
      if (field.max_value != null && value > field.max_value) return `At most ${field.max_value}`;
      return null;
    }
    case 'number': {
      if (typeof value !== 'number' || !Number.isFinite(value)) return 'Enter a number';
      if (field.min_value != null && value < field.min_value) return `At least ${field.min_value}`;
      if (field.max_value != null && value > field.max_value) return `At most ${field.max_value}`;
      return null;
    }
    case 'email':
      if (typeof value !== 'string' || !EMAIL_RE.test(value.trim())) return 'Enter an email address';
      break;
    case 'date':
      if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value) || !parseWallClock(value)) {
        return 'Enter a date';
      }
      break;
    case 'time':
      if (typeof value !== 'string' || !/^([01]\d|2[0-3]):[0-5]\d$/.test(value)) return 'Enter a time';
      break;
    case 'datetime':
      if (typeof value !== 'string' || !parseWallClock(value) || value.length !== 16) {
        return 'Enter a date and time';
      }
      break;
    case 'enum':
    case 'country':
      if (field.choices.length > 0 && !field.choices.some((c) => c.value === value)) {
        return 'Pick one of the listed values';
      }
      break;
    default:
      break;
  }
  if (
    typeof value === 'string' &&
    field.max_length != null &&
    value.length > field.max_length
  ) {
    return `At most ${field.max_length} characters (now ${value.length})`;
  }
  return null;
}
